import {
  autoGenerate,
  cancel,
  complete,
  index,
  post,
  put,
  checkNumber,
  accept,
  get,
  postSendInvoiceForApproval,
} from "src/api/invoice";
import InvoiceRequestForm from "src/modules/purchase-order/components/forms/InvoiceRequestForm.vue";
import InvoiceAutoGenForm from "src/modules/accounts/components/forms/InvoiceAutoGenForm.vue";
import InvoiceForm from "src/modules/accounts/components/forms/InvoiceForm.vue";
import InvoiceDetails from "src/modules/invoices/components/InvoiceDetails.vue";
import { findIndex } from "src/assets/scripts/utilities/helpers";

const getInitialState = () => {
  return {
    isAutoGenerationFailed: false,
    invoices: {
      collection: [],
      completed: [],
      pending: [],
      pod_pending: [],
      pod_received: [],
      pod_rejected: [],
    },
    invoice: {},
  };
};

export const useInvoiceStore = defineStore("invoice", () => {
  const { state: formState, toggle: toggleInvoiceForm } = useDrawer({
    component: InvoiceForm,
  });

  const { state: invoiceRequestFormState, toggle: toggleInvoiceRequestForm } =
    useDrawer({
      component: InvoiceRequestForm,
    });

  const { state: autoGenFormState, toggle: toggleInvoiceAutoGenForm } =
    useDrawer({
      component: InvoiceAutoGenForm,
    });

  const { state: invoiceDetailsFormState, toggle: toggleInvoiceDetailsDrawer } =
    useDrawer({
      component: InvoiceDetails,
    });

  let state = reactive(getInitialState());

  const addInvoice = (invoice) => {
    if (state.invoices?.collection) {
      state.invoices.collection.unshift(invoice);
    } else {
      state.invoices.collection = [invoice];
    }
  };

  const setInvoice = (updatedInvoice) => {
    state.invoices.collection[
      findIndex(state.invoices.collection, updatedInvoice, "_id")
    ] = updatedInvoice;

    let stage_cd =
      updatedInvoice.stage_cd === "in_progress"
        ? "pending"
        : updatedInvoice.stage_cd;

    if (stage_cd) {
      if (state.invoices[stage_cd]) {
        state.invoices[stage_cd][
          findIndex(state.invoices[stage_cd], updatedInvoice, "_id")
        ] = updatedInvoice;
      }
    }

    state.invoice = updatedInvoice;
  };

  const autoGenerateInvoice = async (payload) => {
    try {
      const response = await autoGenerate(payload);

      await getInvoices();

      state.invoice = state.invoices?.pending?.find(
        (invoice) => invoice._id === payload.invoice._id,
      );

      return response;
    } catch (error) {
      state.isAutoGenerationFailed = true;

      console.error(error);

      throw error;
    }
  };

  const cancelInvoice = async (params) => {
    try {
      const response = await cancel(params);

      const { getCounts } = useAccountStore();

      await Promise.all([getCounts(), getInvoices()]);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const completeInvoice = async (invoice) => {
    try {
      const response = await complete(invoice);

      const { getCounts } = useAccountStore();

      await Promise.all([getCounts(), getInvoices()]);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const acceptInvoice = async (invoice) => {
    try {
      const response = await accept(invoice);

      const { getCounts } = useAccountStore();

      await Promise.all([getCounts(), getInvoices()]);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getInvoice = async (id) => {
    const response = await get(id);

    state.invoice = {
      ...(response?.data?.data || {}),
    };

    return response;
  };

  const filters = ref({
    po_id: null,
    stages: null,
  });

  const { filter, pagination, params } = usePagination(filters);

  const getInvoices = async (options = {}) => {
    try {
      options = unref(options);

      if (options?.filters) {
        filters.value = options?.filters;

        delete options.filters;
      }

      const response = await index({
        params: unref(params),
        ...options,
      });

      let stages = options?.filters?.stages || unref(filters)?.stages;

      if (stages === "pending,in_progress") {
        stages = "pending";
      }

      const data = response.data?.data?.data || [];

      if (options?.source === "sales") {
        state.invoices.collection = data;
      } else if (stages) {
        state.invoices[stages] = data;
      } else {
        state.invoices.collection = data;
      }

      pagination.value.rowsNumber = response.data?.data?.count || 0;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const { getEWayBills } = useEWayBillStore();

  const postInvoice = async (invoice) => {
    try {
      const response = await post(invoice);

      const { getCounts } = usePurchaseOrderStore();

      await getCounts(response.data?.data?.po_id?._id);

      addInvoice(response.data?.data);

      getEWayBills({
        filters: {
          descending: true,
          limit: null,
          po_id: response.data?.data?.po_id._id,
          sort: "created_date",
          stage: null,
        },
      });

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const sendInvoiceForApproval = async (invoice) => {
    try {
      const response = await postSendInvoiceForApproval(invoice);

      const { getCounts } = usePurchaseOrderStore();

      await getCounts(response.data?.data?.po_id?._id);

      addInvoice(response.data?.data);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putInvoice = async (invoice) => {
    try {
      const response = await put(invoice);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const addFile = async (file) => {
    let invoiceIndex = findIndex(
      state.invoices.collection,
      { _id: file.invc_id },
      "_id",
    );

    if (invoiceIndex !== -1) {
      state.invoices.collection[invoiceIndex].files.unshift(file);
    }

    invoiceIndex = findIndex(
      state.invoices["Not Started"]?.collection,
      { _id: file.invc_id },
      "_id",
    );

    if (invoiceIndex !== -1) {
      await getInvoices({
        filters: { ...unref(filters), stage: "Not Started" },
      });
    }
  };

  const updateFile = (file) => {
    let fileIndex = findIndex(state.invoice.files, { _id: file._id }, "_id");

    if (fileIndex !== -1) {
      state.invoice.files[fileIndex] = file;
    }
  };

  const isDuplicate = async (number) => {
    const response = await checkNumber(number);

    return response;
  };

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  return {
    formState,
    autoGenFormState,
    invoiceRequestFormState,
    invoiceDetailsFormState,
    toggleInvoiceDetailsDrawer,
    toggleInvoiceForm,
    toggleInvoiceAutoGenForm,
    toggleInvoiceRequestForm,

    filter,
    filters,
    pagination,

    ...toRefs(state),

    setInvoice,

    addFile,
    updateFile,

    getInvoice,
    getInvoices,
    postInvoice,
    putInvoice,

    autoGenerateInvoice,
    cancelInvoice,
    completeInvoice,
    acceptInvoice,
    isDuplicate,
    sendInvoiceForApproval,

    reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInvoiceStore, import.meta.hot));
}
